import { useCallback, useState } from "react";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";

const PROFILE_REMOVE_TOAST_DURATION_MS = 5000;

export function useDeferredProfileRemove(
  handleDeleteProfile?: (itemUUID: string) => boolean | void
) {
  const { showPushToast } = useApplicationToast();
  const [pendingRemoveToastUUIDs, setPendingRemoveToastUUIDs] = useState<
    ReadonlySet<string>
  >(() => new Set());

  const addPendingRemoveToast = useCallback((itemUUID: string) => {
    setPendingRemoveToastUUIDs((prev) => new Set(prev).add(itemUUID));
  }, []);

  const clearPendingRemoveToast = useCallback((itemUUID: string) => {
    setPendingRemoveToastUUIDs((prev) => {
      if (!prev.has(itemUUID)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(itemUUID);
      return next;
    });
  }, []);

  const handleDeleteWithUndoToast = useCallback(
    (itemUUID: string) => {
      if (!itemUUID || !handleDeleteProfile) {
        return;
      }

      let cancelled = false;
      let committed = false;
      addPendingRemoveToast(itemUUID);

      const finalizeRemove = () => {
        if (committed) {
          return;
        }
        committed = true;

        if (cancelled) {
          clearPendingRemoveToast(itemUUID);
          return;
        }

        const enqueued = handleDeleteProfile(itemUUID);
        if (enqueued !== false) {
          clearPendingRemoveToast(itemUUID);
        } else {
          committed = false;
        }
      };

      const cancelPendingRemove = () => {
        cancelled = true;
        clearPendingRemoveToast(itemUUID);
      };

      showPushToast?.({
        message: "Profile has been Removed.",
        actionLabel: "Undo",
        type: "notification",
        autoDismiss: true,
        duration: PROFILE_REMOVE_TOAST_DURATION_MS,
        // Sticky so unrelated push toasts (errors, "profile added", etc.) cannot
        // displace this Undo window. The provider only calls onCancel when another
        // sticky toast replaces it — required so rapid removals finalize the prior
        // pending delete instead of treating replacement as Undo.
        sticky: true,
        onAction: cancelPendingRemove,
        onCancel: finalizeRemove,
        onDismiss: finalizeRemove,
      });
    },
    [
      addPendingRemoveToast,
      clearPendingRemoveToast,
      handleDeleteProfile,
      showPushToast,
    ]
  );

  return {
    handleDeleteWithUndoToast,
    pendingRemoveToastUUIDs,
    hasPendingRemoveToast: pendingRemoveToastUUIDs.size > 0,
  };
}
